import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "@/auth/store";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Modal,
  PasswordInput,
} from "@/components/common";
import { OtpInput } from "@/components/common/OtpInput";
import { useLogin, useMfaResend, useMfaVerify } from "@/hooks/useAuth";

function useMfaCountdown(expiresAtUtc: string | null, onExpire: () => void) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!expiresAtUtc) {
      setSecondsLeft(null);
      return;
    }

    const expiresAt = new Date(expiresAtUtc).getTime();

    function tick() {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        onExpireRef.current();
      }
    }

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAtUtc]);

  return secondsLeft;
}

/** Seconds until the "send a new code" button unlocks (0 = available). */
function useSecondsUntil(isoTime: string | null): number {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!isoTime) {
      setSecondsLeft(0);
      return;
    }

    const target = new Date(isoTime).getTime();
    function tick() {
      setSecondsLeft(Math.max(0, Math.ceil((target - Date.now()) / 1000)));
    }

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isoTime]);

  return secondsLeft;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const login = useLogin();
  const mfaVerify = useMfaVerify();
  const mfaResend = useMfaResend();
  const { mfaPending, clearMfaPending } = useAuthStore();

  function getSafeRedirect(): string {
    const raw = search.redirect;
    if (!raw) {
      return "/";
    }
    if (!raw.startsWith("/") || raw.startsWith("//")) {
      return "/";
    }
    return raw;
  }

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // MFA state
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const resendSecondsLeft = useSecondsUntil(
    mfaPending?.resendAvailableAtUtc ?? null,
  );

  const handleMfaExpire = useCallback(() => {
    clearMfaPending();
    setMfaCode("");
  }, [clearMfaPending]);

  const mfaSecondsLeft = useMfaCountdown(
    mfaPending?.expiresAtUtc ?? null,
    handleMfaExpire,
  );
  const mfaExpired = mfaSecondsLeft !== null && mfaSecondsLeft <= 0;

  function validate(): boolean {
    const next: { email?: string; password?: string } = {};

    if (!email.trim()) {
      next.email = "Adres e-mail jest wymagany";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = "Podaj prawidłowy adres e-mail";
    }

    if (!password) {
      next.password = "Hasło jest wymagane";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    login.reset();

    if (!validate()) {
      return;
    }

    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (response) => {
          if (!("mfaRequired" in response)) {
            void navigate({ to: getSafeRedirect() });
          }
        },
      },
    );
  }

  function handleMfaSubmit(submittedCode?: string) {
    if (!mfaPending) {
      return;
    }
    if (mfaVerify.isPending) {
      return;
    }

    const code =
      submittedCode ?? mfaCode;
    if (!code) {
      return;
    }

    mfaVerify.mutate(
      { mfaToken: mfaPending.mfaToken, code, rememberDevice },
      {
        onSuccess: () => {
          void navigate({ to: getSafeRedirect() });
        },
      },
    );
  }

  function handleMfaClose() {
    clearMfaPending();
    setMfaCode("");
    setRememberDevice(false);
    mfaVerify.reset();
    mfaResend.reset();
  }

  function handleResend() {
    if (!mfaPending || mfaResend.isPending) {
      return;
    }
    mfaVerify.reset();
    setMfaCode("");
    mfaResend.mutate(mfaPending.mfaToken);
  }

  function getErrorMessage(): string | null {
    if (!login.isError) {
      return null;
    }

    const err = login.error as Error & { statusCode?: number };

    if (err.statusCode === 401) {
      return "Nieprawidłowy adres e-mail lub hasło";
    }
    if (err.statusCode && err.statusCode >= 500) {
      return "Wystąpił błąd serwera. Spróbuj ponownie później.";
    }
    return err.message;
  }

  const serverError = getErrorMessage();

  return (
    <div className="flex min-h-screen">
      {/* Left branding panel */}
      <div className="relative hidden overflow-hidden bg-linear-to-br from-adara-700 via-adara-800 to-adara-950 lg:flex lg:w-[45%] lg:flex-col lg:items-center lg:justify-center">
        {/* Decorative circles */}
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-panel/5" />
        <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-panel/5" />
        <div className="absolute top-1/4 right-1/4 h-40 w-40 rounded-full bg-gold-400/10" />

        <div className="relative z-10 flex flex-col items-center px-12">
          <img
            src="/logos/adara-logo-white.svg"
            alt="Adara"
            className="mb-8 w-72"
          />

          <div className="mb-6 h-0.5 w-16 bg-linear-to-r from-gold-300 to-gold-500" />

          <p className="text-center text-sm tracking-widest text-white/70 uppercase">
            Panel Agenta
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-subtle px-4 py-12 sm:px-6 lg:px-8">
        {/* Mobile logo */}
        <div className="mb-8 lg:hidden">
          <img src="/logos/adara-logo.svg" alt="Adara" className="h-12" />
        </div>

        <Card padding="lg" className="w-full max-w-md animate-fade-in">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-main">Zaloguj się</h1>
            <p className="mt-1.5 text-sm text-dim">
              Wprowadź dane logowania, aby kontynuować
            </p>
          </div>

          {serverError && (
            <div className="mb-6">
              <Alert
                variant="error"
                onDismiss={() => {
                  login.reset();
                }}
              >
                {serverError}
              </Alert>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            aria-label="Formularz logowania"
            noValidate
          >
            <div className="flex flex-col gap-5">
              <Input
                label="Adres e-mail"
                type="email"
                autoComplete="email"
                placeholder="jan.kowalski@firma.pl"
                value={email}
                error={errors.email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors((prev) => ({ ...prev, email: undefined }));
                  }
                }}
                leftIcon={
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                }
              />

              <PasswordInput
                label="Hasło"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                error={errors.password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) {
                    setErrors((prev) => ({
                      ...prev,
                      password: undefined,
                    }));
                  }
                }}
                leftIcon={
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                }
              />

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-adara-700 transition-colors hover:text-adara-500"
                >
                  Nie pamiętasz hasła?
                </Link>
              </div>

              <Button
                type="submit"
                fullWidth
                size="lg"
                isLoading={login.isPending}
                className="mt-2"
              >
                Zaloguj się
              </Button>
            </div>
          </form>
        </Card>

        <p className="mt-8 text-center text-xs text-faint">
          &copy; {new Date().getFullYear()} Adara. Wszelkie prawa zastrzeżone.
        </p>
      </div>

      {/* MFA Challenge Modal */}
      <Modal
        isOpen={mfaPending !== null}
        onClose={handleMfaClose}
        title="Kod z wiadomości e-mail"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleMfaSubmit();
          }}
          className="space-y-4"
        >
          {mfaSecondsLeft !== null && mfaSecondsLeft > 0 && (
            <div className="flex items-center justify-between rounded-lg bg-subtle px-3 py-2 text-xs text-dim">
              <span>Ważność kodu</span>
              <span
                className={
                  mfaSecondsLeft <= 30
                    ? "font-medium text-red-600"
                    : "font-medium text-sub"
                }
              >
                {formatCountdown(mfaSecondsLeft)}
              </span>
            </div>
          )}

          <p className="text-sm text-sub">
                Wysłaliśmy 6-cyfrowy kod na adres{" "}
                <span className="font-medium text-main">
                  {mfaPending?.destination ?? "przypisany do konta"}
                </span>
                . Wpisz go, aby dokończyć logowanie.
              </p>
              {import.meta.env.VITE_MOCK_API === "true" && (
                <p className="text-xs text-faint">
                  Tryb demo (mock API): kod to 123456.
                </p>
              )}

              <OtpInput
                value={mfaCode}
                onChange={setMfaCode}
                onComplete={handleMfaSubmit}
              />

              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-dim">Nie dotarł?</span>
                {mfaPending?.resendAvailableAtUtc ? (
                  <button
                    type="button"
                    className="font-medium text-adara-700 hover:text-adara-500 disabled:cursor-not-allowed disabled:text-faint"
                    onClick={handleResend}
                    disabled={resendSecondsLeft > 0 || mfaResend.isPending}
                  >
                    {resendSecondsLeft > 0
                      ? `Wyślij nowy kod (${resendSecondsLeft} s)`
                      : "Wyślij nowy kod"}
                  </button>
                ) : (
                  <span className="text-faint">
                    Limit wysyłek wykorzystany — zaloguj się ponownie
                  </span>
                )}
              </div>

              {mfaResend.isSuccess && !mfaResend.isPending && (
                <Alert variant="success">
                  Wysłaliśmy nowy kod. Poprzedni przestał działać.
                </Alert>
              )}
              {mfaResend.isError && (
                <Alert variant="error">{mfaResend.error.message}</Alert>
              )}

          <Checkbox
            label="Zaufaj temu urządzeniu na 90 dni"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
          />
          <p className="-mt-2 pl-6 text-xs text-faint">
            Nie zaznaczaj na komputerze współdzielonym lub publicznym.
          </p>

          {mfaVerify.isError && (
            <Alert variant="error">{mfaVerify.error.message}</Alert>
          )}

          <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
            <Button type="button" variant="ghost" onClick={handleMfaClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              isLoading={mfaVerify.isPending}
              disabled={mfaExpired || mfaCode.length !== 6}
            >
              Weryfikuj
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
